import React, { useState, useEffect } from 'react';
import { DollarSign, Wallet, Loader2, RefreshCw, Mail, Phone, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const UserInterface = ({ darkMode, user }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  // 修改狀態結構
  const [userQuantities, setUserQuantities] = useState({
    [user?.account]: {
      discount60: 0,
      discount100: 0
    }
  });
  
	// 在組件內添加狀態
	const [showCopyMessage, setShowCopyMessage] = useState(false);
	const [copyMessage, setCopyMessage] = useState('');

	// 複製函數
	const copyToClipboard = (text, type) => {
	  try {
		const textArea = document.createElement('textarea');
		textArea.value = text;
		document.body.appendChild(textArea);
		textArea.select();
		document.execCommand('copy');
		document.body.removeChild(textArea);
		
		setCopyMessage(`已複製${type}`);
		setShowCopyMessage(true);
		
		setTimeout(() => {
		  setShowCopyMessage(false);
		}, 2000);
	  } catch (err) {
		setCopyMessage('複製失敗');
		setShowCopyMessage(true);
		console.error('複製失敗:', err);
	  }
	};
	
// 深淺色模式的顏色配置
const colors = {
  light: {
    background: '#ffffff',
    text: '#333333',
    icon: '#16a34a',      // 淺色模式下的綠色
    shadow: 'rgba(0,0,0,0.15)',
    overlay: 'rgba(0,0,0,0.5)'
  },
  dark: {
    background: '#1a1a1a',
    text: '#ffffff',
    icon: '#22c55e',      // 深色模式下的綠色
    shadow: 'rgba(0,0,0,0.3)',
    overlay: 'rgba(0,0,0,0.7)'
  }
};

  
	// 在 return 之前添加倒數計時狀態和邏輯
	const [countdown, setCountdown] = useState(3);
  const [accountRecords, setAccountRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState({});
  const recordsPerPage = 10;

  useEffect(() => {
	  // 從localStorage恢復狀態
	  const savedStatus = localStorage.getItem('verificationStatus');
	  if (savedStatus) {
		const parsedStatus = JSON.parse(savedStatus);
		
		// 檢查每個驗證的時間戳，如果超過3分鐘就清除
		Object.keys(parsedStatus).forEach(key => {
		  const status = parsedStatus[key];
		  if (status.timestamp) {
			const elapsedTime = Date.now() - status.timestamp;
			if (elapsedTime > 180000) { // 3分鐘
			  delete parsedStatus[key];
			}
		  }
		});
		
		setVerificationStatus(parsedStatus);
	  }
	  
    if (user?.name) {
      fetchAllQuantities();
      fetchAccountRecords();
    }
    let timeoutId;
    
    const updateCountdown = () => {
      setCountdown(prevCount => {
        if (prevCount === 1) return 3;
        return prevCount - 1;
      });
    };

    const startCountdown = () => {
      timeoutId = setTimeout(() => {
        updateCountdown();
        startCountdown();
      }, 1000); // 每個數字顯示 3 秒
    };

    startCountdown();

    return () => clearTimeout(timeoutId);
	

  }, [user?.name]);

  const fetchUserQuantity = async (account, retryCount = 0, maxRetries = 10) => {
  try {
    const response = await fetch(`https://api.mcvanuatu.com/api/quantity/${user.name}`);
    if (!response.ok) {
      throw new Error("Failed to fetch quantity");
    }
    const data = await response.json();
    
    setUserQuantities({
      [user.account]: data
    });
    return true;
  } catch (err) {
    console.error(`Error fetching quantity (attempt ${retryCount + 1}):`, err);
    
    if (retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      return fetchUserQuantity(account, retryCount + 1, maxRetries);
    } else {
      console.error(`Failed to fetch quantity after ${maxRetries} attempts`);
      return false;
    }
  }
};

const fetchAllQuantities = async () => {
  await Promise.all([
    fetchUserQuantity('discount60'),
    fetchUserQuantity('discount100')
  ]);
};

const fetchAccountRecords = async () => {
  try {
    const response = await fetch(
      `https://api.mcvanuatu.com/api/accounts/owner/${user.name}`
    );

    if (!response.ok) {
      throw new Error("找不到該用戶或用戶資料。");
    }

    const data = await response.json();
    if (data.length === 0) {
      throw new Error("你沒有任何數據紀錄。");
    }
    setAccountRecords(data);
    setError("");
  } catch (err) {
    setError(err.message);
    setAccountRecords([]);
  }
};

const handleGetAccount = async () => {
  if (!selectedCard || isLoading) return;
  
  setIsLoading(true);
  setError("");
  
  try {
    const response = await fetch("https://api.mcvanuatu.com/api/accounts/get-account", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: user.name,
        discountType: selectedCard === 'discount60' ? 'ALIPAY60' : 'HKBRNU216'
      }),
    });

    if (!response.ok) {
      throw new Error("當前沒有適用帳戶，請稍後再試。");
    }

    await fetchUserQuantity(selectedCard);
    await fetchAccountRecords();
    setSelectedCard(null);
  } catch (error) {
    console.error('Error getting account:', error);
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};



// Email 驗證相關方法
const emailVerificationMethods = {
  checkEmailCode: async (email, recordId) => {
    try {
      const response = await fetch('https://mailcode.mcvanuatu.com/get-latest-verification-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_address: email,
          password: 'xber1314po'
        })
      });

      if (!response.ok) throw new Error('Failed to fetch verification code');
      
      const data = await response.json();
      if (data.verification_code) {
        setVerificationStatus(prev => {
          const newState = {
            ...prev,
            [`email_${recordId}`]: {
              isLoading: false,
              code: data.verification_code,
              timestamp: Date.now()
            }
          };
          localStorage.setItem('verificationStatus', JSON.stringify(newState));
          return newState;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching email code:', error);
      return false;
    }
  },

  startEmailPolling: (email, recordId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const intervalId = setInterval(async () => {
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        setVerificationStatus(prev => {
          const newState = {
            ...prev,
            [`email_${recordId}`]: { isLoading: false, code: null }
          };
          localStorage.setItem('verificationStatus', JSON.stringify(newState));
          return newState;
        });
        localStorage.removeItem(`emailVerification_${recordId}_startTime`);
        return;
      }
      
      const success = await emailVerificationMethods.checkEmailCode(email, recordId);
      if (success) {
        clearInterval(intervalId);
      }
      attempts++;
    }, 3000);

    return intervalId;
  },

  handleEmailVerification: async (email, recordId) => {
    if (verificationStatus[`email_${recordId}`]?.isLoading) return;
    
    const startTime = Date.now();
    localStorage.setItem(`emailVerification_${recordId}_startTime`, startTime.toString());
    localStorage.setItem(`emailVerification_${recordId}_email`, email);
    
    setVerificationStatus(prev => {
      const newState = {
        ...prev,
        [`email_${recordId}`]: { isLoading: true, code: null }
      };
      localStorage.setItem('verificationStatus', JSON.stringify(newState));
      return newState;
    });

    const intervalId = emailVerificationMethods.startEmailPolling(email, recordId);

    setTimeout(() => {
      clearInterval(intervalId);
      setVerificationStatus(prev => {
        const newState = {
          ...prev,
          [`email_${recordId}`]: { isLoading: false, code: null }
        };
        localStorage.setItem('verificationStatus', JSON.stringify(newState));
        return newState;
      });
      localStorage.removeItem(`emailVerification_${recordId}_startTime`);
      localStorage.removeItem(`emailVerification_${recordId}_email`);
    }, 180000);
  }
};

// Phone 驗證相關方法
const phoneVerificationMethods = {
  checkNumberCode: async (number, id, projectId) => {
    try {
      const response = await fetch('https://api.mcvanuatu.com/api/verify/number', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          number: number,
          projectId: projectId
        })
      });

      if (!response.ok) throw new Error('Failed to fetch number code');
      
      const data = await response.json();
      if (data.code) {
        setVerificationStatus(prev => {
          const newState = {
            ...prev,
            [`number_${id}_${projectId}`]: {
              isLoading: false,
              code: data.code,
              timestamp: Date.now()
            }
          };
          localStorage.setItem('verificationStatus', JSON.stringify(newState));
          return newState;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error:', error);
      return false;
    }
  },

  startNumberPolling: (number, id, projectId) => {
    const maxAttempts = 60;
    let attempts = 0;

    const intervalId = setInterval(async () => {
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        setVerificationStatus(prev => {
          const newState = {
            ...prev,
            [`number_${id}_${projectId}`]: { isLoading: false, code: null }
          };
          localStorage.setItem('verificationStatus', JSON.stringify(newState));
          return newState;
        });
        return;
      }

      const success = await phoneVerificationMethods.checkNumberCode(number, id, projectId);
      if (success) {
        clearInterval(intervalId);
      }
      attempts++;
    }, 3000);

    return intervalId;
  },

  handleNumberVerification: async (number, id, projectId) => {
    if (verificationStatus[`number_${id}_${projectId}`]?.isLoading) return;

    const startTime = Date.now();
    localStorage.setItem(`numberVerification_${id}_${projectId}_startTime`, startTime.toString());
    localStorage.setItem(`numberVerification_${id}_${projectId}_number`, number);

    setVerificationStatus(prev => {
      const newState = {
        ...prev,
        [`number_${id}_${projectId}`]: { isLoading: true, code: null }
      };
      localStorage.setItem('verificationStatus', JSON.stringify(newState));
      return newState;
    });

    const intervalId = phoneVerificationMethods.startNumberPolling(number, id, projectId);

    setTimeout(() => {
      clearInterval(intervalId);
      setVerificationStatus(prev => {
        const newState = {
          ...prev,
          [`number_${id}_${projectId}`]: { isLoading: false, code: null }
        };
        localStorage.setItem('verificationStatus', JSON.stringify(newState));
        return newState;
      });
      localStorage.removeItem(`numberVerification_${id}_${projectId}_startTime`);
      localStorage.removeItem(`numberVerification_${id}_${projectId}_number`);
    }, 180000);
  }
};


// 在組件頂部解構方法
const { handleEmailVerification } = emailVerificationMethods;
const { handleNumberVerification } = phoneVerificationMethods;


// 在組件初始化時添加 useEffect
useEffect(() => {
  // 從 localStorage 恢復狀態
  const savedStatus = localStorage.getItem('verificationStatus');
  if (savedStatus) {
    const parsedStatus = JSON.parse(savedStatus);
    
    // 檢查每個驗證的狀態
    Object.keys(parsedStatus).forEach(key => {
      const status = parsedStatus[key];
      if (status.isLoading) {
        // 如果是郵件驗證
        if (key.startsWith('email_')) {
          const recordId = key.split('_')[1];
          const email = localStorage.getItem(`emailVerification_${recordId}_email`);
          if (email) {
            emailVerificationMethods.startEmailPolling(email, recordId);
          }
        }
        // 如果是號碼驗證
        else if (key.startsWith('number_')) {
          const [_, id, projectId] = key.split('_');
          const number = localStorage.getItem(`numberVerification_${id}_${projectId}_number`);
          if (number) {
            phoneVerificationMethods.startNumberPolling(number, id, projectId);
          }
        }
      }
    });
    
    setVerificationStatus(parsedStatus);
  }

  // 清理超時的驗證狀態
  const cleanup = () => {
    const currentTime = Date.now();
    
    setVerificationStatus(prev => {
      const newState = { ...prev };
      let hasChanges = false;

      Object.keys(newState).forEach(key => {
        const status = newState[key];
        const startTime = localStorage.getItem(`${key.startsWith('email_') ? 'email' : 'number'}Verification_${key.split('_')[1]}_startTime`);
        
        if (startTime && (currentTime - parseInt(startTime) > 180000)) {
          delete newState[key];
          localStorage.removeItem(`${key.startsWith('email_') ? 'email' : 'number'}Verification_${key.split('_')[1]}_startTime`);
          localStorage.removeItem(`${key.startsWith('email_') ? 'email' : 'number'}Verification_${key.split('_')[1]}_${key.startsWith('email_') ? 'email' : 'number'}`);
          hasChanges = true;
        }
      });

      if (hasChanges) {
        localStorage.setItem('verificationStatus', JSON.stringify(newState));
      }
      return hasChanges ? newState : prev;
    });
  };

  const cleanupInterval = setInterval(cleanup, 1000);
  return () => clearInterval(cleanupInterval);
}, []);

const formatDateTime = (dateTimeString) => {
  return dateTimeString ? dateTimeString.replace('T', ' ') : '';
};

const indexOfLastRecord = currentPage * recordsPerPage;
const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
const currentRecords = accountRecords.slice(indexOfFirstRecord, indexOfLastRecord);
const totalPages = Math.ceil(accountRecords.length / recordsPerPage);

  return (
	
    <div className="space-y-4">
	
    {/* 提示框 */}
{showCopyMessage && (
      <>
        {/* 背景遮罩 */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: darkMode ? colors.dark.overlay : colors.light.overlay,
          zIndex: 999,
          animation: 'fadeIn 0.3s forwards'
        }} />

        {/* 提示框 */}
        <div style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: darkMode ? colors.dark.background : colors.light.background,
          color: darkMode ? colors.dark.text : colors.light.text,
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: `0 4px 12px ${darkMode ? colors.dark.shadow : colors.light.shadow}`,
          zIndex: 1000,
          minWidth: '200px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          animation: 'fadeInOut 2s forwards',
          border: darkMode ? '1px solid #333333' : 'none'
        }}>
          {/* 成功圖標 */}
          <svg 
            style={{
              width: '20px',
              height: '20px',
              animation: 'checkmark 0.4s ease-in-out 0.2s forwards',
              opacity: 0,
              transform: 'scale(0.5)',
              color: darkMode ? colors.dark.icon : colors.light.icon
            }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {copyMessage}

          <style>
            {`
              @keyframes fadeInOut {
                0% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
                15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                85% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
              }

              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }

              @keyframes checkmark {
                0% { 
                  opacity: 0;
                  transform: scale(0.5);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.2);
                }
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
              }
            `}
          </style>
        </div>
      </>
    )}
		
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
		{/* ALIPAY60 卡片 */}
        <div
          onClick={() => (userQuantities[user.account]?.discount60 > 0) && setSelectedCard('discount60')}
          className={`relative p-6 rounded-xl border transition-all duration-300 ${
            (userQuantities[user.account]?.discount60 > 0) ? 'cursor-pointer hover:shadow-lg' : 'opacity-50 cursor-not-allowed'
          } ${selectedCard === 'discount60' ? 'ring-2 ring-[#333333]' : ''} ${
            darkMode ? "bg-[#1a1a1a]  border-[#333333]" : "bg-white border-[#333333]"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${darkMode ? "bg-[#1a1a1a]" : "bg-[#f5f5f5]"}`}>
                <DollarSign className={`w-6 h-6 ${darkMode ? "text-[#666666]" : "text-[#333333]"}`} />
              </div>
              <h4 className="font-bold text-lg">ALIPAY60</h4>
            </div>
            <span className={`text-3xl font-bold ${darkMode ? "text-[#ffffff]" : "text-[#333333]"}`}>
              {userQuantities[user.account]?.discount60 || 0}
            </span>
          </div>
        </div>

        {/* HKBRNU216 卡片 */}
        <div
          onClick={() => (userQuantities[user.account]?.discount100 > 0) && setSelectedCard('discount100')}
          className={`relative p-6 rounded-xl border transition-all duration-300 ${
            (userQuantities[user.account]?.discount100 > 0) ? 'cursor-pointer hover:shadow-lg' : 'opacity-50 cursor-not-allowed'
          } ${selectedCard === 'discount100' ? 'ring-2 ring-[#333333]' : ''} ${
            darkMode ? "bg-[#1a1a1a]  border-[#333333]" : "bg-white border-[#333333]"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${darkMode ? "bg-[#1a1a1a]" : "bg-[#f5f5f5]"}`}>
                <Wallet className={`w-6 h-6 ${darkMode ? "text-[#666666]" : "text-[#333333]"}`} />
              </div>
              <h4 className="font-bold text-lg">HKBRNU216</h4>
            </div>
            <span className={`text-3xl font-bold ${darkMode ? "text-[#ffffff]" : "text-[#333333]"}`}>
              {userQuantities[user.account]?.discount100 || 0}
            </span>
          </div>
        </div>
      </div>
	  
  {/* 按鈕和倒數計時在同一行 */}
      <div className="flex items-center justify-between gap-2">
        {/* 獲取帳戶按鈕 */}
        <button
          onClick={handleGetAccount}
          disabled={!selectedCard || isLoading}
          className={`
            px-6 py-3 rounded-xl font-semibold
            transition-all duration-300 flex items-center justify-center
			${!selectedCard || isLoading
			  ? darkMode 
				? 'bg-[#080808] text-white-500 cursor-not-allowed'  // 深色模式下的disabled樣式
				: 'bg-gray-400 text-white cursor-not-allowed'      // 淺色模式下的disabled樣式
			  : darkMode
				? 'bg-[#333333] hover:bg-[#333333] text-[#ffffff]'
				: 'bg-[#e6e6e6] hover:bg-[#cccccc] text-[#333333]'
			}
            shadow-lg hover:shadow-xl
            transform hover:-translate-y-0.5
            w-32
          `}
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            '獲取帳戶'
          )}
        </button>

        {/* 倒數計時動畫 */}
        <div className="flex items-center gap-2 text-lg">
          <AnimatePresence mode="wait">
            <motion.span
              key={countdown}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ 
                duration: 0.5,
                ease: "easeOut" 
              }}
              className={`font-bold text-2xl ${
                darkMode ? "text-[#ffffff]" : "text-[#333333]"
              }`}
            >
              {countdown}
            </motion.span>
          </AnimatePresence>
          <span className={`${
            darkMode ? "text-[#ffffff]" : "text-[#333333]"
          }`}>
            秒刷新一次
          </span>
        </div>
      </div>
      {/* 修改錯誤提示樣式 */}
		{error && (
		  <div className={`
			p-3 rounded-lg mt-2
			${darkMode 
			  ? "bg-red-900/20 text-red-200" 
			  : "bg-red-100 text-red-700"
			}
		  `}>
			{error}
		  </div>
		)}

      <div className="mt-8">
  <div className="hidden md:block overflow-hidden">
    <table className={`min-w-full divide-y border border-separate border-spacing-0  ${darkMode ? "divide-[#333333] border-[#333333] bg-[#1e1e1e]" : "divide-[#F5F5F5] border-[#F5F5F5] bg-white"}`}>
      <thead className={`${darkMode ? "bg-[#333333] text-[#ffffff]" : "bg-[#f5f5f5] text-[#333333]"}`}>
        <tr>
          <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider">電子郵件</th>
          <th className="px-6 py-4 text-center text-sm font-semibold tracking-wider"></th>
          <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider">電話號碼</th>
          <th className="px-6 py-4 text-center text-sm font-semibold tracking-wider"></th>
          <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider">優惠券</th>
          <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider">創建時間</th>
          <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider">解封時間</th>
        </tr>
      </thead>
      <tbody className={`divide-y ${darkMode ? "divide-[#333333] text-[#ffffff]" : "divide-[#F5F5F5] text-[#333333]"}`}>
        {currentRecords.map((record, index) => (
          <tr
            key={index}
            className={`transition-colors duration-200 ${
              darkMode ? "hover:bg-[#1a1a1a] " : "hover:bg-[#f0f0f0]"
            }`}
          >
			{/* Email 欄位 */}
			<td className="px-6 py-4 text-sm whitespace-nowrap">
			  <span 
				className="cursor-pointer"
				onClick={() => copyToClipboard(record.email, '電子郵件')}
			  >
				{record.email}
			  </span>
			</td>
            <td className="px-6 py-4 text-sm text-center">
              <div className="flex justify-center items-center">
                {verificationStatus[`email_${record.id}`]?.isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : verificationStatus[`email_${record.id}`]?.code ? (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    darkMode ? "bg-[#1a1a1a] " : "bg-[#f5f5f5]"
                  }`}>
					<span 
					  className="cursor-pointer hover:text-[#666666]"
					  onClick={() => copyToClipboard(verificationStatus[`email_${record.id}`].code, '驗證碼')}
					>
					  {verificationStatus[`email_${record.id}`].code}
					</span>
                    <RefreshCw
                      className="w-4 h-4 cursor-pointer hover:text-[#666666] transition-colors"
                      onClick={() => handleEmailVerification(record.email, record.id)}
                    />
                  </div>
                ) : (
                  <Mail
                    className="w-5 h-5 cursor-pointer hover:text-[#666666] transition-colors"
                    onClick={() => handleEmailVerification(record.email, record.id)}
                  />
                )}
              </div>
            </td>
		   {/* Phone number 欄位 */}
			<td className="px-6 py-4 text-sm whitespace-nowrap">
			  <span 
				className="cursor-pointer"
				onClick={() => copyToClipboard(record.number, '電話號碼')}
			  >
				{record.number}
			  </span>
			</td>
            <td className="px-6 py-4 text-sm text-center">
              <div className="flex justify-center items-center">
                {verificationStatus[`number_${record.id}_3027`]?.isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : verificationStatus[`number_${record.id}_3027`]?.code ? (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    darkMode ? "bg-[#1a1a1a] " : "bg-[#f5f5f5]"
                  }`}>
       <span 
          className="cursor-pointer hover:text-[#666666]"
          onClick={() => copyToClipboard(verificationStatus[`number_${record.id}_3027`].code, '驗證碼')}
        >
          {verificationStatus[`number_${record.id}`]?.code}
        </span>
                    <RefreshCw
                      className="w-4 h-4 cursor-pointer hover:text-[#666666] transition-colors"
                      onClick={() => handleNumberVerification(record.number, record.id, 3027)}
                    />
                  </div>
                ) : (
                  <MessageCircle
                    className="w-5 h-5 cursor-pointer hover:text-[#666666] transition-colors"
                    onClick={() => handleNumberVerification(record.number, record.id, 3027)}
                  />
                )}
              </div>
            </td>


			
			
            <td className="px-6 py-4 text-sm whitespace-nowrap">{record.discountType}</td>
            <td className="px-6 py-4 text-sm whitespace-nowrap">{formatDateTime(record.createdTime)}</td>
            <td className="px-6 py-4 text-sm whitespace-nowrap">{formatDateTime(record.unlockTime)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>



  {totalPages > 1 && (
    <div className="mt-6 flex justify-center gap-2">
      {Array.from({ length: totalPages }, (_, index) => (
        <button
          key={index}
          onClick={() => setCurrentPage(index + 1)}
          className={`
            px-4 py-2 rounded-lg font-medium transition-all duration-200
            ${currentPage === index + 1
              ? darkMode
                ? "bg-[#333333] text-[#ffffff] shadow-md"
                : "bg-[#333333] text-white shadow-md"
              : darkMode
                ? "bg-[#1a1a1a]  text-[#ffffff] hover:bg-[#333333]"
                : "bg-[#f5f5f5] text-[#333333] hover:bg-[#e6e6e6]"
            }
          `}
        >
          {index + 1}
        </button>
      ))}
    </div>
  )}
</div>

    </div>
	
  );
};

export default UserInterface;